// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  TableRow,
  TableCell,
  Table,
  Divider,
  Checkbox,
  ListItemText,
  ListItem,
  Chip,
  Stack,
  ListItemAvatar,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

export default function CallCenterLiveViewPage() {
  const pageTitle = "Call Center - Live View";
  usePageTitle(pageTitle);
  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);

  const [callCenter, setCallCenter] = useState(null);
  const [onlineAgents, setOnlineAgents] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [minutesAgo, setMinutesAgo] = useState(30);

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "00:00:00";

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    return [
      h.toString().padStart(2, "0"),
      m.toString().padStart(2, "0"),
      s.toString().padStart(2, "0"),
    ].join(":");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/voice/call_centers/${ref}/${minutesAgo}/live.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setCallCenter(result.data.data.call_center);
            setOnlineAgents(result.data.data.online_agents);
            setActiveCalls(result.data.data.active_calls);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage, minutesAgo]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshPage((prev) => !prev);
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mb={5}>
        <MDBox mt={0} mb={1.5} display="flex" justifyContent="space-between">
          <MDBox>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                navigate(`/voice/call-centers/${ref}`);
              }}
            >
              Back
            </Button>
          </MDBox>
          <MDBox align="right" pr={2}>
            <MDTypography variant="body2" fontSize={12}>
              Live View
            </MDTypography>
            <MDTypography variant="h6">{callCenter?.title}</MDTypography>
          </MDBox>
        </MDBox>
        {pageLoaded && (
          <MDBox>
            <Card>
              <MDBox mt={0} display="flex" justifyContent="space-between" p={2} alignItems="center">
                <MDBox>
                  <MDInput
                    select
                    label="Period"
                    onChange={(e) => {
                      setMinutesAgo(parseInt(e.target.value, 10));
                    }}
                    fullWidth
                    SelectProps={{ style: { padding: "12px 0px" } }}
                    sx={{ minWidth: "120px" }}
                    value={minutesAgo}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    <MenuItem value={30}>30 mins</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </MDInput>
                </MDBox>
                <MDBox align="right" pr={2}>
                  <MDButton
                    color="dark"
                    variant="outlined"
                    size="small"
                    startIcon={<Icon>refresh</Icon>}
                    onClick={() => {
                      setRefreshPage(!refreshPage);
                    }}
                  >
                    Refresh
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
            <Grid mt={0.5} container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox>
                  <Card>
                    <MDBox p={2}>
                      <Stack display="flex" gap={2} align="center">
                        <MDBox>
                          <MDTypography fontSize={24}>
                            <Icon color="success">support_agent</Icon>
                          </MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDTypography fontSize={24}>{onlineAgents.length}</MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDTypography variant="body2">Online Agents</MDTypography>
                        </MDBox>
                      </Stack>
                    </MDBox>
                  </Card>
                </MDBox>
                <MDBox mt={3}>
                  <Card>
                    <MDBox p={2}>
                      {onlineAgents.length > 0 ? (
                        <Stack display="flex" alignItems="center" gap={2}>
                          {onlineAgents.map((agent) => (
                            <ListItem>
                              <MDBox display="flex" alignItems="center" gap={3}>
                                <Icon color="success">circle</Icon>
                                <ListItemText
                                  primary={
                                    <MDTypography variant="body2" fontWeight="regular">
                                      {agent.agent}
                                    </MDTypography>
                                  }
                                />
                              </MDBox>
                            </ListItem>
                          ))}
                        </Stack>
                      ) : (
                        <MDBox align="center">
                          <Icon color="error">warning</Icon>
                          <MDTypography variant="body2">
                            No agents are online at the moment.
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox>
                  <Card>
                    <MDBox p={2}>
                      <Stack display="flex" gap={2} align="center">
                        <MDBox>
                          <MDTypography fontSize={24}>
                            <Icon color="success">phone_in_talk</Icon>
                          </MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDTypography fontSize={24}>{activeCalls.length}</MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDTypography variant="body2">Active Calls</MDTypography>
                        </MDBox>
                      </Stack>
                    </MDBox>
                  </Card>
                </MDBox>
                <MDBox mt={3}>
                  <Card>
                    <MDBox p={2}>
                      {activeCalls.length > 0 ? (
                        <Stack gap={2}>
                          {activeCalls.map((call) => (
                            <ListItem>
                              <ListItemAvatar>
                                <Icon color={call.direction === "inbound" ? "warning" : "info"}>
                                  {call.direction === "inbound"
                                    ? "phone_callback"
                                    : "phone_forwarded"}
                                </Icon>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <MDTypography variant="body2">{call.contact}</MDTypography>
                                }
                                secondary={
                                  <MDTypography variant="body2" fontWeight="regular">
                                    {call.agent}
                                  </MDTypography>
                                }
                              />
                              <MDBox align="right">
                                <MDTypography variant="body2" fontSize={12}>
                                  Duration
                                </MDTypography>
                                <MDTypography variant="body2">
                                  {formatDuration(call.duration)}
                                </MDTypography>
                              </MDBox>
                            </ListItem>
                          ))}
                        </Stack>
                      ) : (
                        <MDBox align="center">
                          <Icon color="error">warning</Icon>
                          <MDTypography variant="body2">
                            No active calls at the moment.
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
